import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const homeFor = (role) => ({ patient: "/patient", doctor: "/doctor", admin: "/admin", partner: "/partner", devops: "/devops" }[role] || "/");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!user || (user.role === "patient" && localStorage.getItem("nt_remember") === "1")) return undefined;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(async () => { try { await api.post("/auth/logout"); } catch { /* expired */ } setUser(false); window.location.assign(user.role === "partner" ? "/partners" : user.role === "admin" ? "/admin-login" : user.role === "devops" ? "/q-log" : "/auth?expired=1"); }, 30 * 60 * 1000); };
    const evs = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user]);
  const logout = async () => {
    localStorage.removeItem("nt-act-as");
    try { await api.post("/auth/logout"); } catch { /* session already gone */ }
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, refresh, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const FullLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
    <Loader2 className="h-6 w-6 animate-spin text-[#086B2D]" />
  </div>
);

export const destFor = (role, next) => (next && (next.startsWith(`/${role}`) || next.startsWith("/consultation")) ? next : homeFor(role));

export function PublicOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullLoader />;
  if (user) return <Navigate to={destFor(user.role, new URLSearchParams(loc.search).get("next"))} replace />;
  return children;
}

export function Protected({ role, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to={role === "partner" ? "/partners" : role === "admin" ? "/admin-login" : role === "devops" ? "/q-log" : `/auth?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (role && !(Array.isArray(role) ? role : [role]).includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}
